/**
 * Physical-consistency validation knobs for longitudinal acceleration.
 *
 * Invariant: longitudinal acceleration must agree with the GPS speed trend
 * over any short window. GPS speed cannot lie about accelerating vs braking
 * across ~1.5 s; the fused accelerometer signal can (gravity leak via pitch
 * error). Tuning any value here: edit → `npm run build` in src/PitBrain.Shared
 * → restart/rebuild the clients.
 */

/** Unit: seconds. Half-width of the GPS speed-trend window around each point. Default: 0.7 */
export const ACCEL_CONSISTENCY_WINDOW_S = 0.7

/** Unit: m/s². GPS trend magnitude below this is "no clear trend" — the point is not judged. Default: 0.5 */
export const ACCEL_CONSISTENCY_MIN_TREND_MS2 = 0.5

/** Unit: m/s². Fused accel magnitude below this is too small to judge for sign disagreement. Default: 0.5 */
export const ACCEL_CONSISTENCY_MIN_SIGNAL_MS2 = 0.5

/** Unit: m/s². GPS trend at/above this is unmistakable; a near-zero fused signal against it is flagged. Default: 1.5 */
export const ACCEL_CONSISTENCY_STRONG_TREND_MS2 = 1.5

/** Unit: m/s². GPS trend at/above this is clear enough to judge the fused magnitude proportionally. Default: 0.8 */
export const ACCEL_CONSISTENCY_RELATIVE_TREND_MS2 = 0.8

/** Unit: ratio. Fused magnitude below this fraction of a clear GPS trend is not credible (attenuated/dead signal). Default: 0.4 */
export const ACCEL_CONSISTENCY_RELATIVE_SIGNAL_FRACTION = 0.4

/**
 * Unit: seconds. The relative missing-signal check only flags runs of
 * consecutive candidate points lasting at least this long — shorter
 * excursions are GPS-noise transients, not sustained attenuation. Genuine
 * attenuation defects (a dead sensor axis, gravity-cancelled signal) persist
 * for seconds; GPS-noise-driven trend excursions are transient. Flagging
 * single-sample excursions produces gap confetti in the charts. Default: 0.6
 */
export const ACCEL_CONSISTENCY_SUSTAIN_S = 0.6
